/* ============================================================================
   HR SHARED SCROLL LOCK (BUILD 2, 2026-07-30)

   THE BUG IT FIXES: the cart drawer (this file) and the quiz takeover
   (snippets/hr-quiz-popup.liquid) each wrote `body.style.position/top/width`
   with no coordination. Whichever closed FIRST wiped the other's lock, so the
   page could end up either scroll-locked with nothing on screen, or scrollable
   underneath an open overlay, and `window.scrollTo` could fire with a stale
   offset and teleport the shopper.

   THE FIX: one ref-counted lock keyed by owner. The body is pinned on the
   first acquire and released only when the LAST owner lets go, restoring the
   scroll offset captured at that first acquire. `release()` for a key that
   does not hold the lock is a no-op, so a redundant close() can never unpin the
   page or scroll it to the top.

   Defined idempotently on window so the quiz snippet (an inline script that
   parses BEFORE this deferred file) can create/observe the very same object.
   ========================================================================== */
window.hrScrollLock =
  window.hrScrollLock ||
  (function () {
    var owners = [];
    var savedY = 0;
    return {
      acquire: function (key) {
        if (owners.indexOf(key) !== -1) return;
        if (!owners.length) {
          savedY = window.pageYOffset || document.documentElement.scrollTop || 0;
          var b = document.body.style;
          b.position = "fixed";
          b.top = -savedY + "px";
          b.left = "0";
          b.right = "0";
          b.width = "100%";
          document.body.classList.add("hr-scroll-locked");
        }
        owners.push(key);
      },
      release: function (key) {
        var i = owners.indexOf(key);
        if (i === -1) return; // never unpin a lock this owner does not hold
        owners.splice(i, 1);
        if (owners.length) return;
        var b = document.body.style;
        b.position = "";
        b.top = "";
        b.left = "";
        b.right = "";
        b.width = "";
        document.body.classList.remove("hr-scroll-locked");
        window.scrollTo(0, savedY);
      },
      held: function (key) {
        return owners.indexOf(key) !== -1;
      },
    };
  })();

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    // 2026-09-07: a second <cart-drawer> can exist on a page (a journal post that
    // embeds the buy box) with no overlay of its own; the constructor must not
    // throw there (Observer js_error: "Cannot read properties of null").
    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) overlay.addEventListener("click", this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // A closed drawer must not be a stranded a11y target: it still contains a
    // role="dialog" aria-modal="true" node and a full-viewport scrim.
    if (!this.classList.contains("active")) this.hrSetInert(true);
  }

  hrSetInert(on) {
    if (on) {
      this.setAttribute("aria-hidden", "true");
      this.setAttribute("inert", "");
    } else {
      this.removeAttribute("aria-hidden");
      this.removeAttribute("inert");
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // Un-inert BEFORE anything inside is focused, or trapFocus has nothing to
    // focus and the browser drops focus to <body>.
    this.hrSetInert(false);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    if (!this.classList.contains("active")) {
      // OWNER BUG 2026-08-28 (his emulator screenshot, iPhone 14 Pro Max): the
      // drawer OPENED mid-scroll -- "Your Cart" and the top of the line item
      // were cut off and the shopper landed in the middle of the order
      // summary. `.drawer__inner` is the scroll container and it KEEPS its
      // scrollTop from the previous open (close does not reset it), so anyone
      // who scrolled once got a mid-page open forever after. A sheet must
      // always present from the top.
      const hrInner = this.querySelector(".drawer__inner");
      if (hrInner) hrInner.scrollTop = 0;
      // Ensure opening transition runs smoothly.
      requestAnimationFrame(() => {
        this.classList.add("animate", "active");
      });
    }
    const cartDrawerItems = this.querySelector("cart-drawer-items");
    if (cartDrawerItems) {
      cartDrawerItems.updateCartUpsellToggleState();
      cartDrawerItems.updateCartUpsellVisibility();
    }
    // Dispatch cart:open event
    document.dispatchEvent(new CustomEvent("cart:open"));

    this.addEventListener(
      "transitionend",
      () => {
        // BUILD 2: always trap on the whole sheet. The empty state used to trap
        // on ".drawer__inner-empty" only, which was fine while that panel held
        // its own close button — it no longer does (the duplicate was removed),
        // so trapping there would strand the one dismiss control outside the
        // keyboard loop.
        const containerToTrapFocusOn = document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    // iOS-safe scroll lock: pin the page so it can't scroll or peek behind the drawer.
    // (overflow:hidden alone does NOT stop touch-scroll behind a fixed overlay on iOS Safari.)
    // Ref-counted now, so the quiz takeover's own lock can never wipe this one.
    document.body.classList.add("overflow-hidden");
    window.hrScrollLock.acquire("cart-drawer");
  }

  close() {
    const wasActive = this.classList.contains("active");
    this.classList.remove("active");
    // A dismissal that lands mid-drag must not leave the sheet parked at an
    // inline translateY: clear the gesture state before the exit transition.
    if (window.hrCartCancelDrag) window.hrCartCancelDrag();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
    window.hrScrollLock.release("cart-drawer");
    this.hrSetInert(true);
    if (wasActive) document.dispatchEvent(new CustomEvent("cart:closed"));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  /* ============================================================================
     INSTANT OPTIMISTIC OPEN (owner brief 2026-08-27, LIVE REVENUE BUG).
     MEASURED on the dev theme, WebKit iPhone profile, a throttled mobile
     connection: the drawer painted NOTHING at all until the /cart/add
     response (with its two rendered sections) came back -- first paint and
     "content ready" were the same timestamp, 1.5 to 3.4+ seconds after the
     tap depending on network conditions. For that whole window the shopper
     sees no change at all, which is the exact behaviour the 2026-08-09 notes
     already tie to repeat taps and abandoned carts.

     This opens the drawer AND paints a real, honest placeholder -- the
     product name/plan the buy box has selected, its own already-loaded
     image, and a shimmering (never a guessed) price -- synchronously, before
     any network request for this add has even been sent. `renderContents`
     (below) always runs once the real response lands and replaces this
     wholesale, so nothing here needs to be exact, only truthful about what
     is still loading. Never used to fabricate a price or total: those stay
     shimmer placeholders until the server's own numbers arrive.

     Callers: assets/hr-subscription-box.js's runAddToCart(), right before the
     network request for the add is fired. Safe to call from anywhere else in
     the future the same way -- it is a no-op on top of an already-populated
     drawer beyond opening it (see `alreadyReal` below), so it can never
     clobber real content that is already on screen. */
  showOptimisticAdd(details) {
    details = details || {};
    const itemsWrap = this.querySelector("#CartDrawer-CartItems");
    const alreadyReal = !!(itemsWrap && itemsWrap.querySelector(".cart-item"));

    this.classList.remove("is-empty");
    const inner = this.querySelector(".drawer__inner");
    if (inner) inner.classList.remove("is-empty");
    this.classList.add("hr-drawer-adding");

    if (!alreadyReal && itemsWrap) {
      const row = this.buildOptimisticRow(details);
      if (row) {
        while (itemsWrap.firstChild) itemsWrap.removeChild(itemsWrap.firstChild);
        itemsWrap.appendChild(row);
      }
    }

    // Self-clearing safety net: if the real response never lands (network
    // failure, JS error elsewhere), never leave a placeholder row on screen
    // forever. renderContents() below always clears this first on the normal
    // path, so this timer is a no-op there.
    if (this.hrOptimisticTimer) clearTimeout(this.hrOptimisticTimer);
    this.hrOptimisticTimer = setTimeout(() => this.clearOptimisticAdd(), 9000);

    this.open();
  }

  /* Builds one honest placeholder row from static strings/attributes this
     file owns and from `details`, a plain data object supplied by the
     caller -- never from cart/DOM state that could be stale or attacker-
     controlled. No innerHTML anywhere: every node is created via
     document.createElement and every string goes in via textContent. */
  buildOptimisticRow(details) {
    const title = (details && details.title) || window.variantStrings && window.variantStrings.itemAdded || "Adding your order…";
    const meta = (details && details.meta) || "";
    const imgSrc = details && details.imgSrc;
    const imgAlt = (details && details.imgAlt) || title;

    const wrapper = document.createElement("div");
    wrapper.className = "drawer__cart-items-wrapper hr-optimistic-wrapper";
    wrapper.setAttribute("data-hr-optimistic-row", "1");

    const table = document.createElement("table");
    table.className = "cart-items";
    table.setAttribute("role", "table");
    const tbody = document.createElement("tbody");
    tbody.setAttribute("role", "rowgroup");
    const tr = document.createElement("tr");
    tr.className = "cart-item hr-optimistic-row";
    tr.setAttribute("role", "row");
    tr.setAttribute("aria-busy", "true");

    const tdMedia = document.createElement("td");
    tdMedia.className = "cart-item__media";
    if (imgSrc) {
      const img = document.createElement("img");
      img.className = "cart-item__image";
      img.src = imgSrc;
      img.alt = imgAlt;
      img.width = 150;
      img.height = 150;
      img.decoding = "async";
      tdMedia.appendChild(img);
    } else {
      tdMedia.classList.add("hr-optimistic-shimmer", "hr-optimistic-shimmer--media");
    }

    const tdDetails = document.createElement("td");
    tdDetails.className = "cart-item__details";
    const info = document.createElement("div");
    info.className = "hr-li__info";
    const nameEl = document.createElement("p");
    nameEl.className = "cart-item__name h4 break";
    nameEl.textContent = title;
    info.appendChild(nameEl);
    if (meta) {
      const metaEl = document.createElement("p");
      metaEl.className = "hr-optimistic-meta";
      metaEl.textContent = meta;
      info.appendChild(metaEl);
    }
    tdDetails.appendChild(info);

    const tdTotals = document.createElement("td");
    tdTotals.className = "cart-item__totals right";
    const shimmer = document.createElement("span");
    shimmer.className = "hr-optimistic-shimmer hr-optimistic-shimmer--price";
    shimmer.setAttribute("aria-hidden", "true");
    const srText = document.createElement("span");
    srText.className = "visually-hidden";
    srText.textContent = "Calculating price…";
    tdTotals.appendChild(shimmer);
    tdTotals.appendChild(srText);

    tr.appendChild(tdMedia);
    tr.appendChild(tdDetails);
    tr.appendChild(tdTotals);
    tbody.appendChild(tr);
    table.appendChild(tbody);
    wrapper.appendChild(table);
    return wrapper;
  }

  /* Reverses showOptimisticAdd(). Always called first thing in
     renderContents() so the real, authoritative response can never be
     painted on top of stale placeholder markup. Safe to call when no
     optimistic state exists (renderContents calls it unconditionally). */
  clearOptimisticAdd() {
    if (this.hrOptimisticTimer) {
      clearTimeout(this.hrOptimisticTimer);
      this.hrOptimisticTimer = null;
    }
    if (!this.classList.contains("hr-drawer-adding")) return;
    this.classList.remove("hr-drawer-adding");
    const row = this.querySelector('[data-hr-optimistic-row="1"]');
    if (row && row.parentNode) row.parentNode.removeChild(row);
  }

  renderContents(parsedState) {
    // Always clear any optimistic placeholder first: the response in hand is
    // the real, authoritative cart state and must never render alongside or
    // underneath a fake row (see showOptimisticAdd/clearOptimisticAdd above).
    this.clearOptimisticAdd();
    // P0 (2026-07-28): every step below used to be unguarded, so one missing node
    // or a response without `sections` threw before open() ever ran. The add had
    // already succeeded server-side, so the shopper got a silent success: no
    // drawer, no error, nothing. Re-rendering is now best-effort; opening is not.
    //
    // BUG FIX (2026-08-02, owner screenshot: add-to-cart rendered as a tiny
    // centered near-empty box, no line item / price / checkout visible).
    // ROOT CAUSE: "is-empty" is a class on the <cart-drawer> ROOT element
    // (snippets/cart-drawer.liquid: `<cart-drawer class="drawer{% if cart ==
    // empty %} is-empty{% endif %}">`), and virtually every mobile AND
    // desktop layout rule in component-cart-drawer.css is keyed off
    // `cart-drawer.is-empty` / `cart-drawer:not(.is-empty)` (sizing, display,
    // flex order, and — critically — `cart-drawer.is-empty .drawer__footer{
    // display:none !important }` and `cart-drawer.is-empty .drawer__inner >
    // cart-drawer-items{ display:none }`). This method only ever removed
    // "is-empty" from `.drawer__inner`, a child that never carries that
    // class. On the FIRST add-to-cart from an empty cart, `renderContents`
    // replaces the innerHTML of `#CartDrawer` (a div INSIDE <cart-drawer>)
    // but never touches the outer <cart-drawer> element's classList, so
    // "is-empty" stayed on it forever: the footer (which holds Checkout) and
    // the line-items table stayed `display:none`, and every mobile/desktop
    // sizing rule gated on `:not(.is-empty)` never applied either — leaving
    // only the always-rendered research banner + "Your order" heading
    // visible in a box shrunk to fit that leftover content. Now clears
    // "is-empty" from the actual element that carries it.
    this.classList.remove("is-empty");
    const inner = this.querySelector(".drawer__inner");
    if (inner) inner.classList.remove("is-empty");
    this.productId = parsedState && parsedState.id;
    const sections = (parsedState && parsedState.sections) || {};
    try {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        sections[section.id],
        section.selector
      );
    });
    } catch (e) {
      console.error("cart-drawer: section re-render failed, opening anyway", e);
    }

    setTimeout(() => {
      const overlay = this.querySelector("#CartDrawer-Overlay");
      if (overlay) overlay.addEventListener("click", this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

/* ============================================================================
   HARDWARE / BROWSER BACK DISMISSES THE SHEET (BUILD 2, 2026-07-30)

   ~96% mobile: on Android the hardware back button, and on iOS the edge-swipe
   back gesture, are the FIRST thing a shopper reaches for to dismiss a sheet.
   Before this, back navigated the page out from under an open cart drawer,
   which reads as "the site threw me off the page I was buying on".

   One synthetic history entry is pushed when the drawer opens and popped when
   it closes, so the sheet owns exactly one back step and the URL never changes:
     - back while open  -> popstate closes the drawer, entry already consumed.
     - X / scrim / swipe -> close() fires cart:closed, we history.back() to
       consume OUR entry; the resulting popstate finds the drawer already
       closed and does nothing.
   Skipped in the theme editor, where pushState fights Shopify's own preview
   history.
   ========================================================================== */
(function hrCartDrawerBackButton() {
  if (window.Shopify && window.Shopify.designMode) return;
  if (!window.history || !history.pushState) return;

  var pushed = false;
  var closingFromPop = false;

  document.addEventListener("cart:open", function () {
    var drawer = document.querySelector("cart-drawer");
    if (!drawer || !drawer.classList.contains("active")) {
      // open() adds `active` inside a rAF; wait for it so a no-op open never
      // strands an entry.
      requestAnimationFrame(function () {
        if (!pushed && drawer && drawer.classList.contains("active")) {
          try {
            history.pushState({ hrCartDrawer: 1 }, "");
            pushed = true;
          } catch (e) {}
        }
      });
      return;
    }
    if (pushed) return;
    try {
      history.pushState({ hrCartDrawer: 1 }, "");
      pushed = true;
    } catch (e) {}
  });

  document.addEventListener("cart:closed", function () {
    if (!pushed) return;
    pushed = false;
    if (closingFromPop) return; // the entry was already consumed by the pop
    try {
      history.back();
    } catch (e) {}
  });

  window.addEventListener("popstate", function () {
    var drawer = document.querySelector("cart-drawer");
    if (!drawer || !drawer.classList.contains("active")) return;
    closingFromPop = true;
    pushed = false;
    try {
      drawer.close();
    } finally {
      closingFromPop = false;
    }
  });

  /* bfcache restore: never come back to a page pinned by a lock whose overlay
     is gone. */
  window.addEventListener("pageshow", function (event) {
    if (!event.persisted) return;
    var drawer = document.querySelector("cart-drawer");
    if (drawer && !drawer.classList.contains("active")) {
      window.hrScrollLock.release("cart-drawer");
      document.body.classList.remove("overflow-hidden");
    }
  });
})();

/* ============================================================================
   HR cart-drawer quantity performance (P0, 2026-07-30)

   THE BUG (owner, 425px mobile, screenshot): tapping the quantity minus on the
   last line left the drawer frozen for ~2.5s before the empty state appeared.

   ROOT CAUSE - the stock Dawn path is blocking end to end:
     1. assets/cart.js debounces the qty `change` by ON_CHANGE_DEBOUNCE_TIMER
        (300ms), so NOTHING happens for the first 300ms after the tap.
     2. enableLoading() then puts `cart__items--disabled` (pointer-events:none)
        on the whole item list and un-hides a spinner: the UI is dead, and a
        second minus tap during the flight is swallowed entirely.
     3. POST /cart/change.js asks the server to re-render the ENTIRE `cart-drawer`
        section (69KB of Liquid: research banner, gift block, upsell checkbox,
        discount-code UI, footer) for a change that only affects one row and the
        totals.
     4. The response replaces `.drawer__inner` WHOLESALE, so every image (line
        packshot, app icon, tea packshot, empty-state promo), every inline
        <style> and every custom element in the drawer is destroyed and
        rebuilt/re-decoded on every single +/- tap.
     5. Only then does the shopper see anything change.

   THE FIX (apple-design: respond on the interaction, not on the server) - all
   of it lives in this file so the contested Liquid/CSS files stay untouched:
     - hrOnQuantityIntent() runs in the CAPTURE phase of the same `change`
       event, i.e. on the tap itself, before Dawn's debounced handler. Quantity,
       line total, line savings, subtotal, list-price savings, estimated total,
       the "N items" heading and the header count bubble are all recomputed from
       a cached baseline snapshot of the server-rendered figures. Exact
       arithmetic, no invented numbers: every value scales off the row's own
       per-unit price exactly as the server printed it.
     - Removing the last unit collapses the row instantly (reusing the existing
       window.hrCartCollapseRow) and the empty state then fades in, instead of a
       2.5s freeze followed by a hard swap.
     - Rapid taps coalesce: Dawn's 300ms debounce folds a burst into one
       request, and hrQueued folds anything landing mid-flight into a single
       follow-up for the FINAL quantity rather than a queue of round trips.
     - The re-render is scoped: only `#CartDrawer-CartItems`, the totals block
       and the item-count heading are swapped, so gift images, the research
       banner and the rest of the footer are never rebuilt. The full
       `.drawer__inner` swap is kept ONLY for the empty-cart render, where that
       markup is the point and is cheap. Nodes are moved from the parsed
       response rather than re-parsed through innerHTML.
     - Nothing blocks: `cart__items--disabled` and the spinner overlay are not
       used on the drawer path. A pending row gets a subtle price dim, and only
       if the request has not settled within 220ms.
     - On failure the optimistic change is reverted and a short human message is
       shown in #CartDrawer-CartErrors.
   ========================================================================== */

const HR_CART_SPRING = "cubic-bezier(.32,.72,0,1)";

/* Split a money string ("$1,234.50", "1.234,50 EUR") into its numeric value and
   enough context to rebuild the SAME string with a different number, so the
   shop's currency format is never guessed at. Null when there is no number. */
function hrMoneyParts(text) {
  const source = String(text);
  const match = source.match(/\d[\d.,   ]*\d|\d/);
  if (!match) return null;
  const raw = match[0];
  const decimalMatch = raw.match(/[.,](\d{2})$/);
  const decimalSep = decimalMatch ? raw.charAt(raw.length - 3) : null;
  const groupSep = decimalSep === "," ? "." : ",";
  let normalized = raw.split(groupSep).join("");
  normalized = normalized.replace(/[   ]/g, "");
  if (decimalSep === ",") normalized = normalized.replace(",", ".");
  const value = parseFloat(normalized);
  if (!isFinite(value)) return null;
  return {
    text: source,
    index: match.index,
    length: raw.length,
    grouped: raw.indexOf(groupSep) !== -1,
    decimals: decimalSep ? 2 : 0,
    decimalSep: decimalSep || ".",
    groupSep,
    value,
  };
}

function hrMoneyReplace(parts, value) {
  const rounded = Math.round(Math.max(value, 0) * 100) / 100;
  const fixed = rounded.toFixed(parts.decimals);
  const split = fixed.split(".");
  let integer = split[0];
  const decimal = split[1];
  if (parts.grouped || integer.length > 3) {
    integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, parts.groupSep);
  }
  const rebuilt = decimal ? integer + parts.decimalSep + decimal : integer;
  return (
    parts.text.slice(0, parts.index) +
    rebuilt +
    parts.text.slice(parts.index + parts.length)
  );
}

/* Every money-bearing text node under `root`, so a price cell mixing a price, a
   struck-through list price and a "Save $x" line is handled in one pass. */
function hrMoneyTextNodes(root) {
  const out = [];
  if (!root) return out;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node = walker.nextNode();
  while (node) {
    if (/\d/.test(node.nodeValue)) {
      const parts = hrMoneyParts(node.nodeValue);
      if (parts) out.push({ node, parts });
    }
    node = walker.nextNode();
  }
  return out;
}

function hrFirstMoneyValue(root, skipSelector) {
  if (!root) return null;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node = walker.nextNode();
  while (node) {
    const skipped =
      skipSelector && node.parentElement && node.parentElement.closest(skipSelector);
    if (/\d/.test(node.nodeValue) && !skipped) {
      const parts = hrMoneyParts(node.nodeValue);
      if (parts) return parts.value;
    }
    node = walker.nextNode();
  }
  return null;
}

function hrPrefersReducedMotion() {
  return (
    !!window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/* Move the parsed nodes across instead of re-serialising through innerHTML:
   faster, and it keeps the already-parsed <img> elements out of a second
   HTML parse. */
function hrSwapChildren(target, source) {
  if (!target || !source) return false;
  const imported = document.importNode(source, true);
  target.replaceChildren.apply(target, Array.prototype.slice.call(imported.childNodes));
  return true;
}

/* Injected here rather than in component-cart-drawer.css so this fix stays a
   single-file change while that stylesheet is being edited elsewhere. */
(function hrInjectPendingStyles() {
  if (document.getElementById("hr-cart-optimistic-styles")) return;
  const style = document.createElement("style");
  style.id = "hr-cart-optimistic-styles";
  style.textContent =
    ".cart-drawer .cart-item__price-wrapper," +
    ".cart-drawer .cart-drawer__footer .totals__total-value," +
    ".cart-drawer .cart-drawer__footer .cross-price{" +
    "transition:opacity 160ms ease-out}" +
    ".cart-drawer .cart-item.hr-qty-pending .cart-item__price-wrapper{opacity:.55}" +
    ".cart-drawer.hr-cart-pending .cart-drawer__footer .totals__total-value," +
    ".cart-drawer.hr-cart-pending .cart-drawer__footer .cross-price{opacity:.55}" +
    /* last unit removed: the order summary must not sit there offering checkout
       on a cart that is already empty while the server catches up. It settles
       out on the same curve the row collapses on, then the empty state fades
       up in its place. */
    ".cart-drawer .drawer__footer{" +
    "transition:opacity 220ms cubic-bezier(.32,.72,0,1),transform 260ms cubic-bezier(.32,.72,0,1)}" +
    ".cart-drawer.hr-cart-emptying .drawer__footer{" +
    "opacity:0;transform:translateY(6px);pointer-events:none}" +
    "cart-drawer.is-empty .hr-cart-dialog__order-col," +
    "cart-drawer.is-empty .cart-drawer__footer," +
    "cart-drawer.is-empty .drawer__footer," +
    "cart-drawer[data-hr-empty] .hr-cart-dialog__order-col," +
    "cart-drawer[data-hr-empty] .cart-drawer__footer," +
    "cart-drawer[data-hr-empty] cart-drawer-items," +
    "cart-drawer[data-hr-empty] .drawer__footer{display:none !important}" +
    "cart-drawer[data-hr-empty] .drawer__inner-empty{display:flex !important}" +
    "@media (prefers-reduced-motion:reduce){" +
    ".cart-drawer .cart-item__price-wrapper," +
    ".cart-drawer .drawer__footer," +
    ".cart-drawer .cart-drawer__footer .totals__total-value," +
    ".cart-drawer .cart-drawer__footer .cross-price{transition:none}" +
    ".cart-drawer.hr-cart-emptying .drawer__footer{transform:none}}";
  document.head.appendChild(style);
})();

class CartDrawerItems extends CartItems {
  /* PERF + VISUAL BUG (owner brief 2026-08-05, measured live: a second, fully
     redundant `GET /cart?section_id=cart-drawer` firing ~450-500ms AFTER the
     drawer had already been painted).

     ROOT CAUSE: `assets/cart-drawer.js` (`CartDrawer.renderContents`, called
     directly by product-form.js / product-lumin.js right after `/cart/add`
     resolves) and this class's own `updateQuantity` -> `hrRender` (called
     after `/cart/change`) both already paint the drawer synchronously from a
     response they already have in hand -- no further fetch needed. But the
     base `CartItems.connectedCallback` (assets/cart.js) also subscribes this
     element to the theme's generic `PUB_SUB_EVENTS.cartUpdate` pub/sub, and
     product-form.js's add handler publishes exactly that event. Because this
     class never overrode `onCartUpdate()`, the inherited Dawn implementation
     ran too: a SECOND, independent `fetch(...&section_id=cart-drawer)`
     that `replaceWith()`s `cart-drawer-items` and `.cart-drawer__footer`
     wholesale a beat after the first, correct render had already landed --
     tearing out and rebuilding every image and custom element in the drawer
     a second time. That double render, landing ~450ms apart, is exactly the
     "whole cart becomes very white and weird" flash the owner described.

     This class is always the one responsible for repainting itself (via
     `renderContents` on add, `hrRender` on quantity change); it never needs
     to react to the generic pub/sub echo of its own update by re-fetching and
     re-rendering a second time. No other consumer relies on this override:
     the full-page `cart-items` component (the /cart page) is a different
     element and keeps the base behaviour untouched. */
  onCartUpdate() {}

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.hrBound) return;
    this.hrBound = true;
    this.hrPending = {};
    this.hrBaseline = null;
    this.hrInFlight = null;
    this.hrQueued = null;
    // Capture phase on the host element fires while the event is still
    // travelling DOWN to the <input>, i.e. before the 300ms debounced bubble
    // listener that assets/cart.js registered on this same element.
    this.addEventListener("change", this.hrOnQuantityIntent.bind(this), true);
  }

  hrOnQuantityIntent(event) {
    const input = event.target;
    if (!input || !input.classList || !input.classList.contains("quantity__input")) {
      return;
    }
    const line = input.dataset.index;
    if (!line) return;
    const quantity = parseInt(input.value, 10);
    if (!isFinite(quantity)) return;

    try {
      this.hrEnsureBaseline();
      this.hrPending[line] = quantity;
      if (quantity === 0 && window.hrCartCollapseRow) {
        window.hrCartCollapseRow(document.getElementById(`CartDrawer-Item-${line}`));
      }
      this.hrPaint();
      /* ROOT CAUSE FIX, empty-state speed (owner brief 2026-08-05): stepping the
         last unit's quantity to 0 used to wait on Dawn's ON_CHANGE_DEBOUNCE_TIMER
         (300ms, assets/cart.js) before the /cart/change fetch even started --
         pure dead time stacked in FRONT of network latency, on top of the row's
         own 300ms collapse, which is exactly the stall the owner saw. The
         Remove button (cart-remove-button, assets/cart.js) never had this
         problem: its click handler calls updateQuantity directly, no debounce.
         Firing the same call here, in the CAPTURE listener that runs on the tap
         itself, closes that gap for the stepper path too. Safe to call more
         than once: updateQuantity() coalesces into hrInFlight/hrQueued, so the
         debounced validateQuantity override (cart-drawer.liquid) that still
         fires 300ms later for this same change either finds the request
         in-flight (queues and no-ops once state matches) or, once the empty
         render has already landed and the row is gone, resolves to nothing. */
      if (quantity === 0) {
        this.updateQuantity(
          line,
          0,
          document.activeElement ? document.activeElement.getAttribute("name") : null,
          input.dataset.quantityVariantId
        );
      }
    } catch (e) {
      console.error("cart-drawer: optimistic paint skipped", e);
    }
  }

  /* Snapshot of the figures exactly as the server rendered them. Rebuilt after
     every confirmed render; every optimistic value derives from it, so repeated
     taps never compound rounding on top of their own output. */
  hrEnsureBaseline() {
    if (this.hrBaseline) return this.hrBaseline;
    const drawer = document.getElementById("CartDrawer");
    const footer = drawer && drawer.querySelector(".cart-drawer__footer");
    const rows = {};

    if (drawer) {
      drawer.querySelectorAll(".cart-item").forEach((row) => {
        const input = row.querySelector(".quantity__input");
        if (!input || !input.dataset.index) return;
        const qty = parseInt(input.getAttribute("value"), 10);
        if (!isFinite(qty) || qty <= 0) return;
        const totals = row.querySelector(".cart-item__totals");
        const wrapper = row.querySelector(".cart-item__price-wrapper");
        const priceEl = wrapper && wrapper.querySelector(".price");
        const finalLine = hrFirstMoneyValue(priceEl, "s");
        const strike = wrapper && wrapper.querySelector("s");
        const listLine = strike ? hrFirstMoneyValue(strike) : finalLine;
        rows[input.dataset.index] = {
          qty,
          nodes: hrMoneyTextNodes(totals),
          finalPerUnit: (finalLine == null ? 0 : finalLine) / qty,
          listPerUnit: (listLine == null ? finalLine || 0 : listLine) / qty,
        };
      });
    }

    const totalValues = footer
      ? Array.prototype.slice.call(footer.querySelectorAll(".totals__total-value"))
      : [];
    let savings = null;
    if (footer) {
      footer.querySelectorAll(".totals").forEach((block) => {
        const label = block.querySelector("h2");
        const value = block.querySelector(".cross-price");
        if (!label || !value) return;
        if (/savings/i.test(label.textContent)) {
          savings = { el: value, parts: hrMoneyParts(value.textContent) };
        }
      });
    }

    const bubble = document.querySelector(".cart-count-bubble span[aria-hidden]");
    const heading = document.querySelector(
      "#CartDrawer .hr-cart-dialog__section-heading span:last-child"
    );
    const first = totalValues[0];
    const last = totalValues[totalValues.length - 1];

    this.hrBaseline = {
      rows,
      subtotal: first ? { el: first, parts: hrMoneyParts(first.textContent) } : null,
      total:
        last && last !== first
          ? { el: last, parts: hrMoneyParts(last.textContent) }
          : null,
      savings: savings && savings.parts ? savings : null,
      bubble: bubble
        ? { el: bubble, count: parseInt(bubble.textContent, 10) || 0 }
        : null,
      heading: heading
        ? { el: heading, count: parseInt(heading.textContent, 10) || 0 }
        : null,
    };
    return this.hrBaseline;
  }

  hrPaint() {
    const base = this.hrBaseline;
    if (!base) return;
    let deltaSubtotal = 0;
    let deltaTotal = 0;
    let deltaSavings = 0;
    let deltaCount = 0;

    Object.keys(this.hrPending).forEach((line) => {
      const row = base.rows[line];
      if (!row) return;
      const quantity = this.hrPending[line];
      const delta = quantity - row.qty;
      deltaSubtotal += row.listPerUnit * delta;
      deltaTotal += row.finalPerUnit * delta;
      deltaSavings += (row.listPerUnit - row.finalPerUnit) * delta;
      deltaCount += delta;
      const ratio = quantity / row.qty;
      row.nodes.forEach((entry) => {
        entry.node.nodeValue = hrMoneyReplace(entry.parts, entry.parts.value * ratio);
      });
    });

    if (base.subtotal && base.subtotal.parts) {
      base.subtotal.el.textContent = hrMoneyReplace(
        base.subtotal.parts,
        base.subtotal.parts.value + deltaSubtotal
      );
    }
    if (base.total && base.total.parts) {
      base.total.el.textContent = hrMoneyReplace(
        base.total.parts,
        base.total.parts.value + deltaTotal
      );
    }
    if (base.savings && base.savings.parts) {
      base.savings.el.textContent = hrMoneyReplace(
        base.savings.parts,
        base.savings.parts.value + deltaSavings
      );
    }
    if (base.bubble) {
      base.bubble.el.textContent = String(Math.max(base.bubble.count + deltaCount, 0));
    }
    if (base.heading) {
      const count = Math.max(base.heading.count + deltaCount, 0);
      base.heading.el.textContent = `${count} ${count === 1 ? "item" : "items"}`;
    }

    this.hrSetEmptying(this.hrPredictedCount() === 0);
  }

  /* The item count the shopper has asked for, before the server confirms it. */
  hrPredictedCount() {
    const base = this.hrBaseline;
    if (!base || !Object.keys(base.rows).length) return null;
    let total = 0;
    Object.keys(base.rows).forEach((line) => {
      total +=
        this.hrPending[line] !== undefined ? this.hrPending[line] : base.rows[line].qty;
    });
    return total;
  }

  hrSetEmptying(on) {
    const drawer = document.getElementById("CartDrawer");
    if (drawer) drawer.classList.toggle("hr-cart-emptying", !!on);
    /* MEASURED (owner, dev, 2026-08-05): minus on the last item left the
       shopper staring at a blank white panel with only the X for ~1.5s -- the
       row collapsed and the footer faded at ~700ms, but the EMPTY STATE only
       arrived with the server's section render at ~1.6s. The empty markup
       (.drawer__inner-empty) is ALWAYS in the DOM and is shown purely by the
       `is-empty` class, so the moment the predicted count hits zero we can
       show the real, fully styled empty state instantly and let the server
       response reconcile underneath. hrRevert() calls this with `on=false`,
       which restores the list if the change is rejected. */
    const wrapper = document.querySelector("cart-drawer");
    if (wrapper) {
      wrapper.classList.toggle("is-empty", !!on);
      if (on) wrapper.setAttribute("data-hr-empty", "1");
      else wrapper.removeAttribute("data-hr-empty");
    }
    const inner = wrapper && wrapper.querySelector(".drawer__inner");
    if (inner) inner.classList.toggle("is-empty", !!on);
  }

  /* Dawn calls this when a typed quantity fails the variant's min/max/step
     rules. Drop the optimistic figures with it, and look the input up by the
     drawer's own id (the base method only knows the /cart page's `Quantity-N`,
     so on the drawer it dereferenced null). */
  resetQuantityInput(id) {
    const input =
      this.querySelector(`#Quantity-${id}`) ||
      this.querySelector(`#Drawer-quantity-${id}`);
    if (input) input.value = input.getAttribute("value");
    this.isEnterPressed = false;
    if (this.hrPending && this.hrPending[id] !== undefined) {
      delete this.hrPending[id];
      try {
        this.hrPaint();
      } catch (e) {
        console.error(e);
      }
    }
  }

  hrSetPending(line, on) {
    const row = document.getElementById(`CartDrawer-Item-${line}`);
    if (row) row.classList.toggle("hr-qty-pending", !!on);
    const drawer = document.getElementById("CartDrawer");
    if (drawer) drawer.classList.toggle("hr-cart-pending", !!on);
  }

  /* No full-list pointer-events lock and no spinner overlay on the drawer path:
     the shopper must be able to keep tapping, which is the whole point. */
  enableLoading() {
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute("aria-hidden", false);
    }
  }

  disableLoading() {
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute("aria-hidden", true);
    }
  }

  updateQuantity(line, quantity, name, variantId) {
    try {
      this.hrEnsureBaseline();
      this.hrPending[line] = quantity;
      this.hrPaint();
    } catch (e) {
      console.error("cart-drawer: optimistic paint skipped", e);
    }
    /* Removing the ONLY line: the empty state is knowable at click time, so it
       paints at click time. Independent of the money-parsing baseline above. */
    if (quantity === 0) {
      const rows = document.querySelectorAll("#CartDrawer .cart-item");
      if (rows.length <= 1) this.hrSetEmptying(true);
    }

    // Coalesce: a tap landing mid-flight replaces any earlier queued value, so
    // three fast taps never become three sequential round trips.
    if (this.hrInFlight) {
      this.hrQueued = { line, quantity, name, variantId };
      return this.hrInFlight;
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    const pendingTimer = setTimeout(() => this.hrSetPending(line, true), 220);

    const request = fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body },
    })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        if (parsedState.errors) {
          this.hrRevert(line, parsedState.errors);
          return;
        }
        delete this.hrPending[line];
        this.hrRender(parsedState, line, name, variantId);
      })
      .catch((error) => {
        console.error("cart-drawer: quantity update failed", error);
        this.hrRevert(line, window.cartStrings && window.cartStrings.error);
      })
      .finally(() => {
        clearTimeout(pendingTimer);
        this.hrSetPending(line, false);
        this.hrInFlight = null;
        const queued = this.hrQueued;
        this.hrQueued = null;
        if (queued) this.hrFlush(queued);
      });

    this.hrInFlight = request;
    return request;
  }

  hrFlush(queued) {
    const input = document.getElementById(`Drawer-quantity-${queued.line}`);
    // The line may have been removed or re-indexed by the render that just
    // landed; only re-fire when this row still exists and still disagrees.
    if (!input) return;
    if (parseInt(input.getAttribute("value"), 10) === queued.quantity) return;
    this.updateQuantity(queued.line, queued.quantity, queued.name, queued.variantId);
  }

  hrRevert(line, message) {
    delete this.hrPending[line];
    try {
      this.hrPaint();
    } catch (e) {
      console.error(e);
    }
    this.hrSetEmptying(false);
    const input = document.getElementById(`Drawer-quantity-${line}`);
    if (input) input.value = input.getAttribute("value");
    const row = document.getElementById(`CartDrawer-Item-${line}`);
    if (row && row.dataset.hrLeaving) {
      row.style.cssText = "";
      delete row.dataset.hrLeaving;
    }
    const errors = document.getElementById("CartDrawer-CartErrors");
    if (errors) {
      errors.textContent =
        message || "That did not go through. Check your connection and try again.";
    }
  }

  hrRender(parsedState, line, name, variantId) {
    const sections = parsedState.sections || {};
    const isEmpty = parsedState.item_count === 0;
    const drawerWrapper = document.querySelector("cart-drawer");
    const cartFooter = document.getElementById("main-cart-footer");

    this.classList.toggle("is-empty", isEmpty);
    if (cartFooter) cartFooter.classList.toggle("is-empty", isEmpty);
    if (drawerWrapper) drawerWrapper.classList.toggle("is-empty", isEmpty);

    const errors = document.getElementById("CartDrawer-CartErrors");
    if (errors) errors.textContent = "";

    if (sections["cart-drawer"]) {
      const doc = new DOMParser().parseFromString(
        sections["cart-drawer"],
        "text/html"
      );
      if (isEmpty) {
        this.hrRenderEmpty(doc);
      } else {
        // Surgical: only the regions a quantity change can alter. The gift
        // block and its two images, the research banner and the upsell
        // checkbox are all left standing.
        //
        // BUG FIX (owner-reported live bug, 2026-08-27): "#discount-code-
        // container" was missing from this list. assets/cart-discount-codes-
        // custom.js has no direct way to repaint itself after an Apply/Remove
        // (that file only ever mutates the SERVER'S cart session and then
        // calls this same updateQuantity()/hrRender() as its "force a real
        // repaint" trick, documented there as reusing "the refresh trick
        // cart-discount-codes-custom.js already uses elsewhere"). Because
        // this list never included the discount UI's own container, tapping
        // the X on a discount pill correctly cleared the code server-side
        // (confirmed live: cart.discount_codes went to []) but the PILL
        // ITSELF never repainted -- the shopper kept seeing a "removed" code
        // sitting in the drawer. Adding it here is the fix: it is a real,
        // single, stable wrapper (snippets/cart-discount-codes-custom.liquid,
        // `<div id="discount-code-container">`) present in every fetched
        // "cart-drawer" section response, so swapping its children is exactly
        // as safe as the three regions already here.
        [
          "#CartDrawer-CartItems",
          ".cart-drawer__footer",
          ".hr-cart-dialog__section-heading",
          "#discount-code-container",
        ].forEach((selector) => {
          hrSwapChildren(
            document.querySelector(`#CartDrawer ${selector}`),
            doc.querySelector(selector)
          );
        });
      }
    }

    if (sections["cart-icon-bubble"]) {
      hrSwapChildren(
        document.getElementById("cart-icon-bubble"),
        new DOMParser()
          .parseFromString(sections["cart-icon-bubble"], "text/html")
          .querySelector(".shopify-section")
      );
    }

    // Server is authoritative again: drop the optimistic state. NOTE: not
    // hrSetEmptying(false) -- that also strips `is-empty`, which THIS render
    // just set from the server's own item_count. Calling it here re-showed the
    // order-summary column with $0.00 totals over a confirmed-empty cart
    // (owner's mobile screenshot, 2026-08-05). Only the transitional class is
    // cleared; `is-empty` stays whatever the server said it is.
    this.hrPending = {};
    this.hrBaseline = null;
    {
      const d = document.getElementById("CartDrawer");
      if (d) d.classList.remove("hr-cart-emptying");
      if (!isEmpty) {
        if (drawerWrapper) { drawerWrapper.classList.remove("is-empty"); drawerWrapper.removeAttribute("data-hr-empty"); }
        const inner = drawerWrapper && drawerWrapper.querySelector(".drawer__inner");
        if (inner) inner.classList.remove("is-empty");
      }
    }

    const lineItem =
      document.getElementById(`CartItem-${line}`) ||
      document.getElementById(`CartDrawer-Item-${line}`);
    if (name && lineItem && lineItem.querySelector(`[name="${name}"]`)) {
      if (drawerWrapper) {
        trapFocus(drawerWrapper, lineItem.querySelector(`[name="${name}"]`));
      } else {
        lineItem.querySelector(`[name="${name}"]`).focus();
      }
    } else if (isEmpty && drawerWrapper) {
      // Trap on the sheet, not on the empty panel: the close button is a
      // sibling of that panel now (BUILD 2, single close button).
      const sheet = document.getElementById("CartDrawer") || drawerWrapper;
      trapFocus(
        sheet,
        sheet.querySelector(".drawer__close") || sheet.querySelector("a")
      );
    }

    try {
      this.updateLiveRegions(line, "");
    } catch (e) {
      /* the live-region markup was just replaced; not worth failing on */
    }

    try {
      this.updateProgressBar({
        total_price: parsedState.total_price,
        items_subtotal_price: parsedState.items_subtotal_price,
      });
    } catch (e) {
      console.error("cart-drawer: progress bar update failed", e);
    }

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: "cart-items",
      cartData: parsedState,
      variantId: variantId,
    });
    this.updateCartUpsellToggleState();
    this.updateCartUpsellVisibility(parsedState.item_count);
  }

  /* Last item removed: the row has already collapsed away, so the empty state
     fades up into the space it left instead of hard-swapping the drawer. */
  hrRenderEmpty(doc) {
    const inner = document.querySelector("#CartDrawer .drawer__inner");
    if (!hrSwapChildren(inner, doc.querySelector(".drawer__inner"))) return;
    inner.classList.add("is-empty");
    const empty = inner.querySelector(".drawer__inner-empty");
    if (!empty || hrPrefersReducedMotion()) return;
    empty.style.opacity = "0";
    empty.style.transform = "translateY(10px)";
    requestAnimationFrame(() => {
      empty.style.transition = `opacity 240ms ${HR_CART_SPRING}, transform 280ms ${HR_CART_SPRING}`;
      empty.style.opacity = "1";
      empty.style.transform = "translateY(0)";
    });
    setTimeout(() => {
      empty.style.transition = "";
      empty.style.transform = "";
      empty.style.opacity = "";
    }, 340);
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
